import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GripHorizontal } from 'lucide-react';
import type { TimelineLanePlacement } from './types';

interface TimelineLanePlacementHandleProps {
  placement: TimelineLanePlacement;
  onPlacementChange?: (placement: TimelineLanePlacement) => void;
}

const DRAG_SNAP_THRESHOLD_PX = 16;

const getOppositePlacement = (placement: TimelineLanePlacement): TimelineLanePlacement => (
  placement === 'under-video' ? 'over-video' : 'under-video'
);

const getPlacementActionLabel = (placement: TimelineLanePlacement): string => (
  placement === 'under-video'
    ? 'Move segment lanes over video'
    : 'Move segment lanes under video'
);

const TimelineLanePlacementHandle: React.FC<TimelineLanePlacementHandleProps> = ({
  placement,
  onPlacementChange
}) => {
  const [dragPreviewPlacement, setDragPreviewPlacement] = useState<TimelineLanePlacement | null>(null);
  const suppressNextClickRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const dragCleanupRef = useRef<(() => void) | null>(null);
  const isDisabled = !onPlacementChange;

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const clearClickSuppressionSoon = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      suppressNextClickRef.current = false;
      timeoutRef.current = null;
    }, 0);
  }, []);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (!onPlacementChange) return;

    event.stopPropagation();

    const startY = event.clientY;
    suppressNextClickRef.current = false;
    setDragPreviewPlacement(null);
    dragCleanupRef.current?.();

    const getSnapPlacement = (clientY: number): TimelineLanePlacement | null => {
      const deltaY = clientY - startY;
      if (Math.abs(deltaY) < DRAG_SNAP_THRESHOLD_PX) {
        setDragPreviewPlacement(null);
        return null;
      }

      const nextPlacement = deltaY < 0 ? 'over-video' : 'under-video';
      setDragPreviewPlacement(nextPlacement);
      return nextPlacement;
    };

    let cleanup: () => void = () => undefined;

    const handlePointerMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      getSnapPlacement(moveEvent.clientY);
    };

    const handlePointerUp = (upEvent: PointerEvent) => {
      upEvent.preventDefault();
      const nextPlacement = getSnapPlacement(upEvent.clientY);
      cleanup();

      if (nextPlacement) {
        suppressNextClickRef.current = true;
        onPlacementChange(nextPlacement);
        clearClickSuppressionSoon();
      }
    };

    const handlePointerCancel = () => {
      cleanup();
    };

    cleanup = () => {
      setDragPreviewPlacement(null);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerCancel);
      dragCleanupRef.current = null;
    };

    dragCleanupRef.current = cleanup;
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerCancel);
  }, [clearClickSuppressionSoon, onPlacementChange]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!onPlacementChange) return;

    if (suppressNextClickRef.current) {
      event.preventDefault();
      return;
    }

    onPlacementChange(getOppositePlacement(placement));
  }, [onPlacementChange, placement]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const actionLabel = getPlacementActionLabel(placement);
  const handleClassName = [
    'timeline-lane-placement-handle',
    `is-${placement}`,
    dragPreviewPlacement ? 'is-dragging' : '',
    dragPreviewPlacement ? `is-preview-${dragPreviewPlacement}` : ''
  ].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={handleClassName}
      data-testid="timeline-lane-placement-handle"
      data-placement={placement}
      data-drag-preview-placement={dragPreviewPlacement ?? undefined}
      aria-label={actionLabel}
      aria-pressed={placement === 'over-video'}
      title={actionLabel}
      disabled={isDisabled}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <GripHorizontal aria-hidden="true" size={16} strokeWidth={2.4} />
    </button>
  );
};

export default TimelineLanePlacementHandle;
